import React from 'react';
import { Card } from '../ui/card';
import { Lightning, CheckCircle, Clock, CalendarBlank, Warning } from '@phosphor-icons/react';

function StatPill({ color, label, value, pulse }) {
  return (
    <div className={`flex items-center gap-2 justify-end ${pulse ? 'animate-pulse' : ''}`}>
      <span className="text-xs font-semibold text-white/80">{label}</span>
      <div className={`${color} text-white text-xs font-black px-2 py-0.5 rounded-full min-w-[24px] text-center`}>{value}</div>
    </div>
  );
}

function StatBadge({ icon: Icon, color, value, label, pulse }) {
  return (
    <div className={`flex items-center gap-1 ${pulse ? 'animate-pulse' : ''}`}>
      <Icon size={14} className={color} weight="fill" />
      <span className="text-xs font-black text-white">{value}</span>
      <span className="text-[10px] text-white/60">{label}</span>
    </div>
  );
}

export function GlobalProgressHero({ stats, globalProgress }) {
  return (
    <Card className="overflow-hidden border-0 shadow-lg" data-testid="global-progress-card">
      <div className={`p-5 sm:p-6 ${
        globalProgress >= 75 ? 'bg-gradient-to-br from-emerald-600 to-emerald-700' :
        globalProgress >= 40 ? 'bg-gradient-to-br from-[#FF6B00] to-orange-700' :
        'bg-gradient-to-br from-[#111827] to-gray-800'
      }`}>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Lightning size={20} className="text-white/80" weight="fill" />
              <span className="text-sm font-semibold text-white/80 uppercase tracking-wide">Avancement global du chantier</span>
            </div>
            <div className="flex items-baseline gap-1 mb-4">
              <span className="text-5xl sm:text-6xl font-black text-white leading-none" data-testid="global-progress-value">
                {globalProgress}
              </span>
              <span className="text-2xl font-bold text-white/70">%</span>
            </div>
            <div className="w-full h-5 bg-white/20 rounded-full overflow-hidden backdrop-blur-sm">
              <div className="h-full rounded-full bg-white transition-all duration-1000 ease-out"
                style={{ width: `${globalProgress}%` }} data-testid="global-progress-bar" />
            </div>
          </div>
          <div className="hidden sm:flex flex-col gap-1.5 text-right min-w-[100px]">
            <StatPill color="bg-emerald-400" label="Terminées" value={stats.completed} />
            <StatPill color="bg-blue-400" label="En execution" value={stats.inProgress} />
            <StatPill color="bg-amber-300" label="A realiser" value={stats.pending} />
            {stats.delayed > 0 && <StatPill color="bg-red-400" label="En retard" value={stats.delayed} pulse />}
          </div>
        </div>
        <div className="flex justify-between mt-3 sm:hidden">
          <StatBadge icon={CheckCircle} color="text-emerald-300" value={stats.completed} label="terminées" />
          <StatBadge icon={Clock} color="text-blue-300" value={stats.inProgress} label="en cours" />
          <StatBadge icon={CalendarBlank} color="text-amber-300" value={stats.pending} label="a realiser" />
          {stats.delayed > 0 && <StatBadge icon={Warning} color="text-red-300" value={stats.delayed} label="en retard" pulse />}
        </div>
        {stats.delayed > 0 && (
          <div className="mt-3 px-3 py-2 bg-red-500/30 backdrop-blur-sm rounded-lg border border-red-400/30 flex items-center gap-2" data-testid="delay-alert-banner">
            <Warning size={18} className="text-red-200 flex-shrink-0 animate-pulse" weight="fill" />
            <span className="text-xs font-bold text-red-100">
              {stats.delayed} tâche(s) en retard critique — intervention requise
            </span>
          </div>
        )}
      </div>
    </Card>
  );
}
